// import { Timestamp } from 'firebase/firestore';

import { LexoRank } from 'lexorank';

import Const from './Constants';

// LexoRank fractional-indexing helpers, shared by drag-and-drop ordering across
// stores (tasks, meeting checkables). beforeRank/afterRank may be null to
// indicate top-of-list or bottom-of-list inserts.
export const rankBetween = (beforeRank, afterRank) => {
  if (!beforeRank && !afterRank) return LexoRank.middle().toString();
  if (!beforeRank) return LexoRank.parse(afterRank).genPrev().toString();
  if (!afterRank) return LexoRank.parse(beforeRank).genNext().toString();
  return LexoRank.parse(beforeRank).between(LexoRank.parse(afterRank)).toString();
};

export const rankCompare = (a, b) => {
  return a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0;
};

export const getAvatarUrl = (name) => name ? `/avatars/${name}.png` : '';

export const lowercaseFirstLetter = (str) => {
  if (!str) return '';
  return str.charAt(0).toLowerCase() + str.slice(1);
}

export const notifUserSignIn = async (auth, setSubscription) => {
    try {
        // If user is not signed in, no need to notify the sign in?
        if(!auth.currentUser) {
          return;
        }

        const idToken = await auth.currentUser.getIdToken(true); // True to refresh token on sigh in
        const theURL = process.env.REACT_APP_BASE_URL + '/signin';
        const response = await fetch(theURL, {
        headers: new Headers({
            Authorization: idToken
        })
        });
        const sub = await response.json();

        setSubscription({
          state: sub.state,
          subscriptionId: sub.subscriptionId, // unused in front end for now
          timestamp: sub.timestamp // unused in front end for now
        });

    } catch (error) {
        console.error(error.message);
    }
}

export const handleAuthStateChangeCallback = async (auth, isSignedIn, setIsSignedIn, onSignIn, setSubscription) => {
    // This should be always false it seems
    if(!isSignedIn) {
      const unregisterAuthObserver = auth.onAuthStateChanged(async user => {
        setIsSignedIn(!!user);

        // Notify backend of the sign-in FIRST and wait for it: /signin creates the
        // user record (for a brand-new user) before we read it. Otherwise onSignIn's
        // /user and /user/config calls race /signin and can hit a not-yet-created
        // user (404 on /user, empty views). seeding stays async on the backend.
        if(user) {
          await notifUserSignIn(auth, setSubscription);
        }

        // Notify caller on sign in results
        onSignIn(!!user);
      });
      return () => { unregisterAuthObserver(); } // Make sure we un-register Firebase observers when the component unmounts.
    } else {
        // Notify caller on sign in results
        onSignIn(false);
    }
  }

export const handleUpdateNote = async (auth, noteId, title, body, group) => {
  try {
    if(title === null && body === null && group === null) {
      throw new Error('handleUpdateNote: All params are null');
    }

    const idToken = await auth.currentUser.getIdToken();
    const theURL = process.env.REACT_APP_BASE_URL + '/notes';

    let reqBody = {
      noteId
    };

    if(title !== null) {
      reqBody[Const.sTitle] = title;
    }

    if(body !== null) {
      reqBody[Const.sBody] = body;
    }

    if(group !== null) {
      reqBody[Const.sGroup] = group;
    }
  
    await fetch(theURL, {
      method: Const.HttpPatch,
      headers: new Headers({
        Authorization: idToken,
        Accept: Const.AppJson,
        [Const.ContentType]: Const.AppJson
      }),
      body: JSON.stringify(reqBody)
    });

  } catch (error) {
    console.error(error.message);
  }
}

export const handleUpdateMeeting = async (auth, meetingId, title, group) => {
  try {
    if(title === null && group === null) {
      throw new Error('handleUpdateMeeting: All params are null');
    }

    const idToken = await auth.currentUser.getIdToken();
    const theURL = process.env.REACT_APP_BASE_URL + Const.sMeetingsPath;

    let reqBody = {
      meetingId
    };

    if(title !== null) {
      reqBody[Const.sTitle] = title;
    }

    if(group !== null) {
      reqBody[Const.sGroup] = group;
    }
  
    await fetch(theURL, {
      method: Const.HttpPatch,
      headers: new Headers({
        Authorization: idToken,
        Accept: Const.AppJson,
        [Const.ContentType]: Const.AppJson
      }),
      body: JSON.stringify(reqBody)
    });

  } catch (error) {
    console.error(error.message);
  }
};

export const isEqual = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const  fsDateToString = (date) => {
  // Convert Firestore timestamp to JavaScript Date object
  const d = new Date(date._seconds * 1000); // Multiply seconds by 1000 to convert to milliseconds

  // Now format the date as you wish, for example using `toLocaleDateString`
  const formattedDate = d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return formattedDate;
}

export const getTodayFSDate = () => {
  const now = new Date();  // Get the current date and time

  const seconds = Math.floor(now.getTime() / 1000);  // Convert the current time to seconds
  const nanoseconds = (now.getTime() % 1000) * 1000000;  // Calculate the remaining nanoseconds

  // Create a Firestore Timestamp object in their format
  return {_seconds: seconds, _nanoseconds: nanoseconds};  
}

export const sameDay = (date1, date2) => {
  // Convert Firestore-like Timestamps to JavaScript Date objects
  const d1 = new Date(date1._seconds * 1000);
  const d2 = new Date(date2._seconds * 1000);

  // Compare the year, month, and day of both dates
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

export const sortByTimestamp = (array, ascending = true) => {
  array.sort((a, b) => {
    const aMs = a.timestamp?.toMillis?.() ?? 0;
    const bMs = b.timestamp?.toMillis?.() ?? 0;
    return ascending ? aMs - bMs : bMs - aMs;
  });
};

export const sortByTitle = async (array) => {
  array.sort((a, b) => {
    if (!a || !a.title) return 1;  // Treat null/undefined as 'greater' to push them to the end
    if (!b || !b.title) return -1; // Same logic for `b`
    
    return a.title.localeCompare(b.title);
  });
}

export const showSuccessToast = (toast, title, description, status) => {
  toast({
    title,
    description,
    status: status === undefined ? 'success' : status,
    duration: 3000,     // Duration in milliseconds
    isClosable: true,   // Show close button
    position: 'top'
  });
}

export const actualSizePhotoUrl = (url) => {
  // Prevent null access later on
  if(!url) {
    return '';
  }

  // Find the last occurrence of the '=' character
  const index = url.lastIndexOf('=');
  
  // If '=' is found, truncate the URL up to that point, otherwise return the original URL
  return index !== -1 ? url.substring(0, index) : url;
}

export const handleSubscribe = (userId) => {
  const checkoutURL = `https://flowhero.lemonsqueezy.com/buy/889a6ac3-1dc4-42ae-8765-a164bf7f9845?discount=0&checkout[custom][user_id]=${userId}`;
  window.location.href = checkoutURL;
}

export const handleUpdateUserConfig = async (auth, userConfig, setUserConfig, configPart) => {
  try {
    // We first update local state of the config to be in sync with the backend.
    const copyUserConfig = JSON.parse(JSON.stringify(userConfig));
    const key = Object.keys(configPart)[0];
    copyUserConfig.local[key] = configPart[key];
    setUserConfig(copyUserConfig);

    // Update the backend.
    const idToken = await auth.currentUser.getIdToken();
    const theURL = process.env.REACT_APP_BASE_URL + '/user/config';
    const response = await fetch(theURL, {
      method: Const.HttpPatch,
      headers: new Headers({
        Authorization: idToken,
        Accept: Const.AppJson,
        [Const.ContentType]: Const.AppJson
      }),
      body: JSON.stringify(configPart)
    });

    // Check if the request was successful
    if(!response.ok) {
      throw new Error('Failed to update user config');
    }
  } catch (error) {
    console.error(error.message);
  }
}

export const handleUpdateTask = async (auth, taskId, title = null, description = null, done = null, status = null, rank = null, epicId = null, importance = null, urgency = null, assigneeType = undefined, assigneeId = undefined) => {
  try {
    if(title === null &&
       description === null &&
       done === null &&
       status === null &&
       rank === null &&
       epicId === null &&
       importance === null &&
       urgency === null &&
       assigneeType === undefined &&
       assigneeId === undefined) {
      throw new Error('handleUpdateTask: All params are null');
    }

    const idToken = await auth.currentUser.getIdToken();
    const theURL = process.env.REACT_APP_BASE_URL + '/tasks';

    let body = {
      taskId
    };

    if(title !== null) {
      body[Const.sTitle] = title;
    }

    if(description !== null) {
      body[Const.sDescription] = description;
    }

    if(done !== null) {
      body[Const.sDone] = done;
    }

    if(status !== null) {
      body[Const.sStatus] = status;
    }

    if(rank !== null) {
      body[Const.sRank] = rank;
    }

    if(epicId !== null) {
      body[Const.sEpicId] = epicId;
    }

    if(importance !== null) {
      body[Const.sImportance] = importance;
    }

    if(urgency !== null) {
      body[Const.sUrgency] = urgency;
    }

    if(assigneeType !== undefined) {
      body['assigneeType'] = assigneeType;
    }

    if(assigneeId !== undefined) {
      body['assigneeId'] = assigneeId;
    }

    await fetch(theURL, {
      method: Const.HttpPatch,
      headers: new Headers({
        Authorization: idToken,
        Accept: Const.AppJson,
        [Const.ContentType]: Const.AppJson
      }),
      body: JSON.stringify(body)
    });

  } catch (error) {
    console.error(error.message);
  }
}
